import {FetchService} from "dc-services/fetch.service";
import {shopLanguageCode} from "./shopUrl";

/**
 * Public view of a shared AI Vision Studio rendering (magic link, #233679).
 * The link is https://www.eila.de/?aivision=<token>; only the exact secret token grants access.
 * frontend_eila.php renders the page frame (#aiVisionShareView) in the normal layout instead of
 * the page content, this script fills it with the rendering.
 */
const TEXTS = {
    de: {
        title: "Geteiltes Rendering",
        studio: "eila AI Vision Studio",
        missing: "Dieses Rendering ist nicht mehr verfügbar.",
        note: "Renderings sind KI-generiert und nicht verbindlich",
        download: "Download",
        share: "Teilen",
        unit: "Zur Einheit",
        category: "Kategorie",
        dimensions: "Maße (L × B × H)",
        floors: "Etagen",
        copied: "Link kopiert",
        home: "Zur Startseite",
        views: {three_quarter: "Dreiviertelansicht", front: "Front", left: "Seite links", right: "Seite rechts", back: "Heck", interior: "Innenansicht"},
    },
    en: {
        title: "Shared rendering",
        studio: "eila AI Vision Studio",
        missing: "This rendering is no longer available.",
        note: "Renderings are AI generated and not binding",
        download: "Download",
        share: "Share",
        unit: "View unit",
        category: "Category",
        dimensions: "Dimensions (L × W × H)",
        floors: "Floors",
        copied: "Link copied",
        home: "To the homepage",
        views: {three_quarter: "Three-quarter view", front: "Front", left: "Left side", right: "Right side", back: "Rear", interior: "Interior"},
    },
};

function isEnglish() {
    return shopLanguageCode() === "en";
}

function text(key) {
    const tc = window.GLOBALS?.tc || {};
    return tc[`eila_aivision_share_${key}`] || TEXTS[isEnglish() ? "en" : "de"][key];
}

function formatDate(value) {
    const date = new Date(String(value).replace(" ", "T"));
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString(isEnglish() ? "en-GB" : "de-DE");
}

function element(tag, className, content) {
    const node = document.createElement(tag);
    if (className) {
        node.className = className;
    }
    if (content !== undefined) {
        node.textContent = content;
    }
    return node;
}

/** title and facts in one line: "Name  eila AI Vision Studio · unit · date" */
function renderHeader(view, title, meta) {
    const header = element("div", "aiVisionShareView__header");
    header.append(
        element("h1", "aiVisionShareView__title", title),
        element("p", "aiVisionShareView__meta", [text("studio"), meta].filter(Boolean).join(" · "))
    );
    view.append(header);
}

function isLocalPath(value) {
    return typeof value === "string" && value.startsWith("/") && !value.startsWith("//");
}

function formatMeters(value) {
    return Number(value).toLocaleString(isEnglish() ? "en-GB" : "de-DE", {maximumFractionDigits: 2});
}

/** fact sheet of the unit like on the product page: picture, name, summary, attributes, dimensions */
function renderUnitCard(unit) {
    if (!unit || !unit.name) {
        return null;
    }
    const card = element("aside", "aiVisionShareView__card");
    if (isLocalPath(unit.imageUrl)) {
        const picture = element("img", "aiVisionShareView__cardImage");
        picture.src = unit.imageUrl;
        picture.alt = unit.name;
        picture.loading = "lazy";
        card.append(picture);
    }
    card.append(element("h2", "aiVisionShareView__cardName", unit.name));
    if (unit.summary) {
        card.append(element("p", "aiVisionShareView__cardSummary", unit.summary));
    }

    // same markup as the product page: the unit (m², persons) comes from the attributesList styles
    const attributes = Array.isArray(unit.attributes) ? unit.attributes.filter((entry) => /^[A-Z_]+$/.test(entry.code || "")) : [];
    if (attributes.length > 0) {
        const list = element("ul", "attributesList");
        attributes.forEach((entry) => {
            const item = element("li", `attributesList__item attributesList__item--${entry.code}`);
            const icon = element("i", `icon icon-attribute-${entry.code}`);
            icon.setAttribute("aria-hidden", "true");
            item.append(icon, element("span", "", entry.value));
            list.append(item);
        });
        card.append(list);
    }

    const facts = [];
    if (unit.category) {
        facts.push([text("category"), unit.category]);
    }
    if (Array.isArray(unit.dimensions) && unit.dimensions.length === 3) {
        facts.push([text("dimensions"), `${unit.dimensions.map(formatMeters).join(" × ")} m`]);
    }
    if (unit.floors) {
        facts.push([text("floors"), String(unit.floors)]);
    }
    if (facts.length > 0) {
        const list = element("dl", "aiVisionShareView__cardFacts");
        facts.forEach(([label, value]) => list.append(element("dt", "", label), element("dd", "", value)));
        card.append(list);
    }

    return card;
}

export async function initAiVisionShare() {
    const view = document.getElementById("aiVisionShareView");
    const token = view?.dataset.token || "";
    if (!view || !/^[a-f0-9]{48}$/.test(token)) {
        return;
    }
    view.append(element("div", "aiVisionShareView__placeholder"));

    let shared = null;
    try {
        const response = await FetchService.post("/em/aiVisionStudio/getSharedRendering", {token});
        shared = response?.success ? response.data : null;
    } catch {
        shared = null;
    }

    view.innerHTML = "";
    view.removeAttribute("aria-busy");

    if (!shared) {
        renderHeader(view, text("title"));
        view.append(element("p", "aiVisionShareView__meta", text("missing")));
        const home = element("a", "button button--primary", text("home"));
        home.href = "/";
        const actions = element("div", "aiVisionShareView__actions");
        actions.append(home);
        view.append(actions);
        return;
    }

    // without a title the brand names the rendering, at least the unit
    const name = shared.title || shared.brandName || shared.unitName || text("title");
    document.title = `${name} – ${text("studio")}`;
    renderHeader(view, name, [name !== shared.unitName ? shared.unitName : "", formatDate(shared.createdAt)].filter(Boolean).join(" · "));

    // left: large image, views and note; right: fact sheet of the unit with download and share
    // (below on small screens). Without a fact sheet the actions stay below the image.
    const body = element("div", "aiVisionShareView__body");
    const main = element("div", "aiVisionShareView__main");
    const stage = element("div", "aiVisionShareView__stage");
    const figure = element("figure", "aiVisionShareView__figure");
    const image = element("img", "aiVisionShareView__image");
    image.alt = name;
    figure.append(image);
    stage.append(figure);
    main.append(stage, element("p", "aiVisionShareView__note", text("note")));
    body.append(main);
    view.append(body);

    const actions = element("div", "aiVisionShareView__actions");
    const download = element("a", "button button--primary", text("download"));
    const share = element("button", "linkWithIcon aiVisionShareView__share");
    share.type = "button";
    const shareIcon = element("i", "icon icon-share");
    shareIcon.setAttribute("aria-hidden", "true");
    const shareLabel = element("span", "", text("share"));
    share.append(shareIcon, shareLabel);
    // system share sheet where available (mobile), otherwise the link is copied
    share.addEventListener("click", async () => {
        const url = window.location.href;
        if (navigator.share) {
            await navigator.share({title: document.title, url}).catch(() => undefined);
            return;
        }
        await navigator.clipboard?.writeText(url).catch(() => undefined);
        shareLabel.textContent = text("copied");
        setTimeout(() => { shareLabel.textContent = text("share"); }, 2500);
    });
    actions.append(download, share);
    const card = renderUnitCard(shared.unit);
    if (card) {
        actions.className = "aiVisionShareView__cardActions";
        card.append(actions);
        body.append(card);
        body.classList.add("has-card");
    } else if (isLocalPath(shared.unitUrl)) {
        // no fact sheet: at least the link to the product page of the unit
        const unit = element("a", "linkWithIcon aiVisionShareView__unit");
        unit.href = shared.unitUrl;
        const unitIcon = element("i", "icon icon-arrow-right");
        unitIcon.setAttribute("aria-hidden", "true");
        unit.append(element("span", "", shared.unitName ? `${text("unit")}: ${shared.unitName}` : text("unit")), unitIcon);
        actions.append(unit);
    }

    const viewNames = TEXTS[isEnglish() ? "en" : "de"].views;
    // the link shares the whole order: all views as thumbnails below the image, the download is one ZIP with all of them
    const views = Array.isArray(shared.views) ? shared.views.filter((entry) => entry.url) : [];
    if (views.length > 1) {
        download.href = `/em/aiVisionStudio/downloadSharedRenderings?token=${encodeURIComponent(token)}`;
        download.textContent = `${text("download")} (${views.length})`;
    }
    const show = (url) => {
        image.src = url;
        if (views.length <= 1) {
            download.href = url;
            download.download = `eila-vision-${Date.now()}.${/\.png(\?|$)/i.test(url) ? "png" : "jpg"}`;
        }
    };
    show(shared.url);

    if (views.length > 1) {
        const strip = element("div", "aiVisionShareView__strip");
        views.forEach((entry) => {
            const thumb = element("button", "aiVisionShareView__thumb");
            thumb.type = "button";
            thumb.setAttribute("aria-pressed", String(entry.url === shared.url));
            const thumbImage = element("img");
            thumbImage.src = entry.url;
            thumbImage.alt = "";
            thumb.append(thumbImage, element("span", "aiVisionShareView__thumbLabel", viewNames[entry.view] || ""));
            // no focus on pointer clicks: the browser would scroll a partly visible thumbnail into view
            thumb.addEventListener("mousedown", (event) => event.preventDefault());
            thumb.addEventListener("click", () => {
                show(entry.url);
                strip.querySelectorAll(".aiVisionShareView__thumb").forEach((other) => other.setAttribute("aria-pressed", String(other === thumb)));
            });
            strip.append(thumb);
        });
        stage.append(strip);
    }
    if (!card) {
        main.append(actions);
    }
}
